import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
}

function StatCard({ title, value, icon, color = 'primary', subtitle }: StatCardProps) {
  const getColor = (color: string) => {
    const colors = {
      primary: '#1976d2',
      secondary: '#dc004e',
      success: '#2e7d32',
      warning: '#ed6c02',
      error: '#d32f2f',
      info: '#0288d1',
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: getColor(color) + '15',
              color: getColor(color),
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 600 }}>
          {value.toLocaleString()}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function ContentMetrics() {
  const [includeBlocked, setIncludeBlocked] = useState(false);
  const [days, setDays] = useState<number | null>(null); // null = all time

  const { data, isLoading, error } = useQuery({
    queryKey: ['contentMetrics', days, includeBlocked],
    queryFn: () => statsApi.getContentMetrics(days, includeBlocked),
    refetchInterval: 60000, // Обновляем каждую минуту
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки content метрик: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Нет данных для отображения
      </Alert>
    );
  }

  const { transcriptions, chat_prompts, context } = data;

  return (
    <Box>
      {/* Period and Filter Selectors */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {/* Blocked Users Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={includeBlocked}
              onChange={(e) => setIncludeBlocked(e.target.checked)}
              color="primary"
            />
          }
          label="Считать заблокированных пользователей"
        />

        {/* Period Selector */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Период</InputLabel>
          <Select
            value={days === null ? 'all' : days}
            label="Период"
            onChange={(e) => {
              const value = e.target.value;
              setDays(value === 'all' ? null : (value as number));
            }}
          >
            <MenuItem value="all">Всё время</MenuItem>
            <MenuItem value={1}>Сегодня</MenuItem>
            <MenuItem value={7}>7 дней</MenuItem>
            <MenuItem value={30}>30 дней</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Main Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Транскрипций"
            value={transcriptions.total}
            icon={<DescriptionIcon />}
            color="primary"
            subtitle="Успешно обработано"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Chat промптов"
            value={chat_prompts.total}
            icon={<ChatIcon />}
            color="secondary"
            subtitle="Всего запросов к чату"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Среднее транскрипций"
            value={transcriptions.average_per_active_user}
            icon={<PersonIcon />}
            color="info"
            subtitle="На активного пользователя"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Среднее промптов"
            value={chat_prompts.average_per_active_user}
            icon={<PersonIcon />}
            color="warning"
            subtitle="На активного пользователя"
          />
        </Grid>
      </Grid>

      {/* Detailed Info Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <DescriptionIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Статистика транскрипций
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Успешных
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {transcriptions.total.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Неудачных
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="error.main">
                      {transcriptions.total_failed.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Прерванных
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main">
                      {transcriptions.total_interrupted.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Всего попыток
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {transcriptions.total_sessions.toLocaleString()}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Success Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={transcriptions.success_rate}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: transcriptions.success_rate >= 80 ? '#2e7d32' : transcriptions.success_rate >= 50 ? '#ed6c02' : '#d32f2f',
                            borderRadius: 5,
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 50 }}>
                      {transcriptions.success_rate}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ChatIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Статистика чата
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Всего Chat промптов: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="secondary.main">
                    {chat_prompts.total.toLocaleString()}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  В среднем на активного пользователя: {' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="warning.main">
                    {chat_prompts.average_per_active_user}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Активных пользователей: {' '}
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {context.active_users.toLocaleString()}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Всего зарегистрировано: {' '}
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {context.total_registered_users.toLocaleString()}
                  </Typography>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
